import { useState, useEffect, useRef, useCallback } from 'react';
import { collection, addDoc, query, where, orderBy, getDocs, Timestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../lib/firebase';
import { useAuth } from '../contexts/AuthContext';
import type { BugReportDocument, BugReportType, BugReportStatus } from '@schemas/firestore';

const REPORT_TYPES: { value: BugReportType; label: string; icon: string }[] = [
    { value: 'bug', label: 'Bug', icon: '🐛' },
    { value: 'feature', label: 'Feature Request', icon: '✨' },
    { value: 'improvement', label: 'Improvement', icon: '💡' },
];

const STATUS_LABELS: Record<BugReportStatus, { label: string; color: string }> = {
    open: { label: 'Open', color: 'bg-blue-500' },
    acknowledged: { label: 'Acknowledged', color: 'bg-yellow-500' },
    in_progress: { label: 'In Progress', color: 'bg-purple-500' },
    resolved: { label: 'Resolved', color: 'bg-green-500' },
    wont_fix: { label: "Won't Fix", color: 'bg-gray-500' },
};

interface MyReport {
    id: string;
    title: string;
    type: BugReportType;
    status: BugReportStatus;
    createdAt: Date;
}

export default function ReportBugPage() {
    const { user } = useAuth();
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [type, setType] = useState<BugReportType>('bug');
    const [screenshot, setScreenshot] = useState<File | null>(null);
    const [screenshotPreview, setScreenshotPreview] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [myReports, setMyReports] = useState<MyReport[]>([]);
    const [loadingReports, setLoadingReports] = useState(true);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const dropZoneRef = useRef<HTMLDivElement>(null);

    // Load user's previous reports
    useEffect(() => {
        async function loadMyReports() {
            if (!user) return;

            try {
                const reportsQuery = query(
                    collection(db, 'bugReports'),
                    where('reportedBy', '==', user.uid),
                    orderBy('createdAt', 'desc')
                );
                const snapshot = await getDocs(reportsQuery);
                const reports = snapshot.docs.map(doc => {
                    const data = doc.data();
                    return {
                        id: doc.id,
                        title: data.title,
                        type: data.type as BugReportType,
                        status: data.status as BugReportStatus,
                        createdAt: data.createdAt?.toDate() || new Date(),
                    };
                });
                setMyReports(reports);
            } catch (err) {
                console.error('Error loading reports:', err);
            } finally {
                setLoadingReports(false);
            }
        }

        loadMyReports();
    }, [user, success]);

    // Handle paste event for screenshots
    useEffect(() => {
        const handlePaste = (e: ClipboardEvent) => {
            const items = e.clipboardData?.items;
            if (!items) return;

            for (const item of items) {
                if (item.type.startsWith('image/')) {
                    const file = item.getAsFile();
                    if (file) {
                        setScreenshot(file);
                        setScreenshotPreview(URL.createObjectURL(file));
                    }
                    break;
                }
            }
        };

        document.addEventListener('paste', handlePaste);
        return () => document.removeEventListener('paste', handlePaste);
    }, []);

    // Handle drag and drop
    const handleDragOver = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (dropZoneRef.current) {
            dropZoneRef.current.classList.add('border-primary-500', 'bg-primary-500/10');
        }
    }, []);

    const handleDragLeave = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (dropZoneRef.current) {
            dropZoneRef.current.classList.remove('border-primary-500', 'bg-primary-500/10');
        }
    }, []);

    const handleDrop = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (dropZoneRef.current) {
            dropZoneRef.current.classList.remove('border-primary-500', 'bg-primary-500/10');
        }

        const files = e.dataTransfer.files;
        if (files.length > 0 && files[0].type.startsWith('image/')) {
            setScreenshot(files[0]);
            setScreenshotPreview(URL.createObjectURL(files[0]));
        }
    }, []);

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file && file.type.startsWith('image/')) {
            setScreenshot(file);
            setScreenshotPreview(URL.createObjectURL(file));
        }
    };

    const removeScreenshot = () => {
        setScreenshot(null);
        if (screenshotPreview) {
            URL.revokeObjectURL(screenshotPreview);
        }
        setScreenshotPreview(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!user || !title.trim() || !description.trim()) return;

        setSubmitting(true);
        setError(null);

        try {
            let screenshotUrl: string | undefined;
            let screenshotPath: string | undefined;

            // Upload screenshot if provided
            if (screenshot) {
                const reportId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
                const ext = screenshot.name.split('.').pop() || 'png';
                screenshotPath = `bugReports/${reportId}/screenshot.${ext}`;
                const storageRef = ref(storage, screenshotPath);
                await uploadBytes(storageRef, screenshot);
                screenshotUrl = await getDownloadURL(storageRef);
            }

            // Create bug report document
            const reportData: Omit<BugReportDocument, 'id'> = {
                title: title.trim(),
                description: description.trim(),
                type,
                screenshotUrl,
                screenshotPath,
                reportedBy: user.uid,
                reporterEmail: user.email || '',
                reporterName: user.displayName || user.email?.split('@')[0] || 'Anonymous',
                status: 'open',
                affectedPage: window.location.pathname,
                createdAt: Timestamp.now(),
                updatedAt: Timestamp.now(),
            };

            await addDoc(collection(db, 'bugReports'), reportData);

            // Reset form
            setTitle('');
            setDescription('');
            setType('bug');
            removeScreenshot();
            setSuccess(true);

            // Hide success message after 5 seconds
            setTimeout(() => setSuccess(false), 5000);
        } catch (err) {
            console.error('Error submitting report:', err);
            setError('Failed to submit report. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="p-6 lg:p-8 max-w-4xl">
            {/* Header */}
            <div className="mb-8 animate-fade-in">
                <h1 className="text-3xl font-display font-bold text-white">
                    Report a Bug or Request a Feature
                </h1>
                <p className="text-secondary-400 mt-1">
                    Help us improve! Submit bugs you've found or features you'd like to see.
                </p>
            </div>

            {/* Success Message */}
            {success && (
                <div className="mb-6 p-4 bg-green-500/20 border border-green-500/50 rounded-lg animate-fade-in">
                    <div className="flex items-center gap-3">
                        <span className="text-2xl">✅</span>
                        <div>
                            <p className="text-green-400 font-medium">Report Submitted!</p>
                            <p className="text-green-300/80 text-sm">
                                Thank you for your feedback. We'll review it soon.
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* Error Message */}
            {error && (
                <div className="mb-6 p-4 bg-red-500/20 border border-red-500/50 rounded-lg">
                    <p className="text-red-400">{error}</p>
                </div>
            )}

            {/* Report Form */}
            <form onSubmit={handleSubmit} className="card mb-8 animate-slide-up">
                <h2 className="text-xl font-semibold text-white mb-6">New Report</h2>

                {/* Report Type */}
                <div className="mb-6">
                    <label className="block text-sm font-medium text-secondary-300 mb-3">
                        Report Type
                    </label>
                    <div className="flex gap-3">
                        {REPORT_TYPES.map(({ value, label, icon }) => (
                            <button
                                key={value}
                                type="button"
                                onClick={() => setType(value)}
                                className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-all ${type === value
                                        ? 'border-primary-500 bg-primary-500/20 text-white'
                                        : 'border-secondary-600 text-secondary-400 hover:border-secondary-500 hover:text-secondary-300'
                                    }`}
                            >
                                <span>{icon}</span>
                                <span>{label}</span>
                            </button>
                        ))}
                    </div>
                </div>

                {/* Title */}
                <div className="mb-6">
                    <label htmlFor="title" className="block text-sm font-medium text-secondary-300 mb-2">
                        Title
                    </label>
                    <input
                        id="title"
                        type="text"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        placeholder={type === 'bug' ? 'Brief description of the issue' : 'What would you like to see?'}
                        className="w-full px-4 py-3 bg-secondary-800 border border-secondary-600 rounded-lg text-white placeholder-secondary-500 focus:outline-none focus:border-primary-500 transition-colors"
                        required
                    />
                </div>

                {/* Description */}
                <div className="mb-6">
                    <label htmlFor="description" className="block text-sm font-medium text-secondary-300 mb-2">
                        Description
                    </label>
                    <textarea
                        id="description"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        placeholder={
                            type === 'bug'
                                ? 'What happened? What did you expect to happen? Steps to reproduce...'
                                : 'Describe your idea in detail. What problem would it solve?'
                        }
                        rows={5}
                        className="w-full px-4 py-3 bg-secondary-800 border border-secondary-600 rounded-lg text-white placeholder-secondary-500 focus:outline-none focus:border-primary-500 transition-colors resize-none"
                        required
                    />
                </div>

                {/* Screenshot Upload */}
                <div className="mb-6">
                    <label className="block text-sm font-medium text-secondary-300 mb-2">
                        Screenshot (Optional)
                    </label>
                    <p className="text-secondary-500 text-sm mb-3">
                        Paste (Ctrl+V), drag & drop, or click to upload
                    </p>

                    {screenshotPreview ? (
                        <div className="relative inline-block">
                            <img
                                src={screenshotPreview}
                                alt="Screenshot preview"
                                className="max-h-64 rounded-lg border border-secondary-600"
                            />
                            <button
                                type="button"
                                onClick={removeScreenshot}
                                className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
                                aria-label="Remove screenshot"
                            >
                                ×
                            </button>
                        </div>
                    ) : (
                        <div
                            ref={dropZoneRef}
                            onDragOver={handleDragOver}
                            onDragLeave={handleDragLeave}
                            onDrop={handleDrop}
                            onClick={() => fileInputRef.current?.click()}
                            className="border-2 border-dashed border-secondary-600 rounded-lg p-8 text-center cursor-pointer hover:border-secondary-500 transition-colors"
                        >
                            <span className="text-4xl block mb-2">📷</span>
                            <p className="text-secondary-400">
                                Drop an image here, paste from clipboard, or click to browse
                            </p>
                        </div>
                    )}

                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleFileSelect}
                        className="hidden"
                    />
                </div>

                {/* Submit Button */}
                <button
                    type="submit"
                    disabled={submitting || !title.trim() || !description.trim()}
                    className="btn-primary w-full flex items-center justify-center gap-2"
                >
                    {submitting ? (
                        <>
                            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            Submitting...
                        </>
                    ) : (
                        <>
                            <span>📨</span>
                            Submit Report
                        </>
                    )}
                </button>
            </form>

            {/* My Reports */}
            <div className="card animate-slide-up" style={{ animationDelay: '0.1s' }}>
                <h2 className="text-xl font-semibold text-white mb-6">My Reports</h2>

                {loadingReports ? (
                    <div className="flex items-center justify-center py-8">
                        <div className="w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full animate-spin" />
                    </div>
                ) : myReports.length === 0 ? (
                    <p className="text-secondary-400 text-center py-8">
                        You haven't submitted any reports yet.
                    </p>
                ) : (
                    <div className="space-y-3">
                        {myReports.map(report => (
                            <div
                                key={report.id}
                                className="flex items-center justify-between p-4 bg-secondary-800/50 rounded-lg"
                            >
                                <div className="flex items-center gap-4">
                                    <span className="text-xl">
                                        {REPORT_TYPES.find(t => t.value === report.type)?.icon || '📋'}
                                    </span>
                                    <div>
                                        <p className="text-white font-medium">{report.title}</p>
                                        <p className="text-secondary-500 text-sm">
                                            {report.createdAt.toLocaleDateString('en-US', {
                                                month: 'short',
                                                day: 'numeric',
                                                year: 'numeric',
                                            })}
                                        </p>
                                    </div>
                                </div>
                                <span
                                    className={`px-3 py-1 rounded-full text-xs font-medium text-white ${STATUS_LABELS[report.status]?.color || 'bg-gray-500'
                                        }`}
                                >
                                    {STATUS_LABELS[report.status]?.label || report.status}
                                </span>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
